import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import {
  ControlContainer,
  FormGroup,
  FormGroupDirective,
} from '@angular/forms';
import { HelperService, MessServiceService } from '@app/shared/common';
import { Store } from '@ngrx/store';

@Component({
  selector: 'app-content-layout',
  templateUrl: './content-layout.component.html',
  styleUrls: ['./content-layout.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
})
export class ContentLayoutComponent implements OnInit {
  /**
   * * General
   * @pageId Id of current WEBSITE page
   * @sectionId Id of current WEBSITE section
   * @dataForPage
   * @websiteUrl URL of website
   * @languages All website languages
   * @subscriptions Array of subscriptions
   * @publishTypeOptions Array for publish type options
   */
  pageId: any;
  sectionId: any;
  dataForPage: any;
  websiteUrl: string;
  languages: any[];
  showScheduleDate = false;
  now: Date = new Date();
  subscriptions: Subscription[] = [];
  publishTypeOptions: { value: number; name: string }[] = [];
  /**
   * * Outputs
   * @handleSubmit Event when user click SAVE
   */
  @Output() handleSubmit = new EventEmitter<any>();
  /**
   * * Inputs
   * @loading Show/hide BLOCK-UI component
   * @formGroup FormGroup
   * @skeletonLoad Show/hide skeleton
   * @headerTitle Current page title
   * @headerType 'main' is for layouts where you show for example tables, 'inner' is type of layout where you edit some data from table
   * @backPath Back path for back button
   * @showPreviewButton Preview button will lead you to website page which you are currently editing
   * @showAddButton When you set 'main' for @headerType layout you can show add button that will add /add to your current url
   * @showOnOff Show switch that will show hide your website page/section
   * @blockUiText Text that will be shown in BLOCK-UI component
   * @saveBtnText Save button text
   * @layoutType This property is necessary to extract the correct id from the url (Types of id in url: id,postId,sectionId)
   * @dataForPage All data fetched from API for current app page
   */
  @Input() loading = false;
  @Input() formGroup: FormGroup;
  @Input() showSidebar = false;
  @Input() skeletonLoad = false;
  @Input() headerTitle: string;
  @Input() headerType: 'main' | 'inner' | 'none';
  @Input() backPath: any[];
  @Input() showPreviewButton = false;
  @Input() sheduleOption = false; // to activate shedule option in visibility dropdown
  @Input() showAddButton = false;
  @Input() showVisibilityOption = true;
  @Input() showOnOff = true;
  @Input() blockUiText: string;
  @Input() saveBtnText = 'Save';
  @Input() layoutType: 'page' | 'section' | 'post';
  @Input() set pageData(value: any) {
    if (value) {
      this.dataForPage = value;
      if (this.dataForPage.schedule_date) {
        this.showScheduleDate = true;
      }

      if (this.showPreviewButton && value.locale[this.translate.currentLang]) {
        this.websiteUrl = `${this.websiteUrl}/${this.translate.currentLang}/${
          value.locale[this.translate.currentLang].slug
        }`;
      }
    }
  }

  constructor(
    private messService: MessServiceService,
    private helperService: HelperService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private store: Store<{ config: any }>
  ) {
    /**
     * Get config from ngrx store
     */
    this.subscriptions.push(
      store.select('config').subscribe((res) => {
        this.websiteUrl = res.apiUrl;
        this.languages = res.languages;
      })
    );
  }

  /* ====================================
  *              LIFECYCLES
  ======================================= */

  ngOnInit(): void {
    /**
     * Get page ID
     */
    if (this.layoutType === 'section') {
      this.pageId = +this.route.snapshot.params['sectionId'];
    } else if (this.layoutType === 'post') {
      this.pageId = +this.route.snapshot.params['postId'];
    } else {
      this.pageId = +this.route.snapshot.params['id'];
    }

    this.publishTypeOptions = [
      { name: this.translate.instant('STATIC.private'), value: 0 },
      { name: this.translate.instant('STATIC.public'), value: 1 },
      { name: this.translate.instant('STATIC.schedule'), value: 2 },
    ];

    /**
     * Remove schedule option if post is default.
     */
    if (!this.sheduleOption) {
      this.publishTypeOptions.pop();
    }
  }

  /* ====================================
  *             MAIN FEATURES
  ======================================= */

  onSubmitForm() {
    this.handleSubmit.emit(true);
  }

  /**
   * @param event Check event
   * @param pageId Id of page/post/section that you want to show/hide
   */
  onChangePageStatus(pageId: number) {
    this.subscriptions.push(
      this.helperService.toggleStatus(pageId).subscribe({
        next: (res) => {
          this.messService.addSuccess({
            detail: res.message,
          });
        },
        error: (err) => {
          this.messService.addError({
            detail: err.error.message,
          });
        },
      })
    );
  }

  /* ====================================
  *             HELPERS
  ======================================= */

  onChangePublishAction(value) {
    this.formGroup.get('schedule_date')?.setValue('');
    this.showScheduleDate = value === 2;
  }
}
